import { cn } from "@faire/prototype-playground/lib/utils";
import { Button } from "@faire/slate/components/button";
import { Tag } from "@faire/slate/components/tag";
import { Toggle } from "@faire/slate/components/toggle";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

// ---------- Brand Sidebar ----------

function ChevronIcon({ expanded }: { expanded?: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className={cn("transition-transform", expanded ? "rotate-90" : "")}
    >
      <path
        d="M6 4L10 8L6 12"
        stroke="#757575"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function BrandSidebar() {
  return (
    <div className="bg-fs-surface-primary sticky top-0 flex h-screen w-64 shrink-0 flex-col border-r border-[#dfe0e1]">
      <div className="flex items-center justify-between px-6 py-8">
        <img src="/images/faire-logo.svg" alt="Faire" className="w-[88px]" />
        <div className="flex items-center gap-2">
          <button className="relative flex h-10 w-10 items-center justify-center rounded-full hover:bg-[#f7f7f7]">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#333"
              strokeWidth="1.5"
            >
              <path
                d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.73 21a2 2 0 0 1-3.46 0"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[#E53935]" />
          </button>
          <button className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E8D5C4] text-sm font-medium text-[#333]">
            AC
          </button>
        </div>
      </div>
      <nav className="flex flex-1 flex-col gap-1 overflow-auto">
        <a
          href="#"
          className="px-6 py-1.5 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          Home
        </a>
        <a
          href="#"
          className="flex items-center justify-between px-6 py-1.5 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          <span>Orders</span>
          <div className="flex items-center gap-2">
            <span className="text-fs-text-primary-inverse rounded-full bg-[#333] px-2 text-xs">
              1
            </span>
            <ChevronIcon />
          </div>
        </a>
        <a
          href="#"
          className="px-6 py-1.5 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          Messages
        </a>
        <a
          href="#"
          className="flex items-center justify-between px-6 py-1.5 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          <span>Products</span>
          <ChevronIcon />
        </a>
        <a
          href="#"
          className="flex items-center justify-between px-6 py-1.5 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          <span>Customers</span>
          <ChevronIcon />
        </a>
        <a
          href="#"
          className="flex items-center justify-between px-6 py-1.5 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          <span>Faire Direct</span>
          <ChevronIcon />
        </a>
        <a
          href="#"
          className="flex items-center justify-between px-6 py-1.5 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          <span>Marketing</span>
          <ChevronIcon expanded />
        </a>
        <a
          href="#"
          className="py-1.5 pr-6 pl-10 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          Campaigns
        </a>
        <a
          href="#"
          className="py-1.5 pr-6 pl-10 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          Automations
        </a>
        <a
          href="#"
          className="py-1.5 pr-6 pl-10 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          Promotions
        </a>
        <div className="relative mx-2">
          <div className="absolute inset-0 rounded-lg bg-[#f7f7f7]" />
          <a
            href="#"
            className="relative flex items-center justify-between py-1.5 pr-6 pl-8 text-sm font-medium text-[#333]"
          >
            <span>Promoted Listings</span>
            <span className="rounded-sm border border-[#333] px-1.5 py-0.5 text-[11px] text-[#333]">
              New
            </span>
          </a>
        </div>
        <a
          href="#"
          className="py-1.5 pr-6 pl-10 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          Insider Partnership
        </a>
        <a
          href="#"
          className="py-1.5 pr-6 pl-10 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          Events
        </a>
        <a
          href="#"
          className="flex items-center justify-between px-6 py-1.5 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          <span>Analytics</span>
          <ChevronIcon />
        </a>
        <a
          href="#"
          className="flex items-center justify-between px-6 py-1.5 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          <span>My shop</span>
          <ChevronIcon />
        </a>
        <a
          href="#"
          className="flex items-center justify-between px-6 py-1.5 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          <span>Settings</span>
          <ChevronIcon />
        </a>
      </nav>
      <div className="border-t border-[#dfe0e1] py-4">
        <a
          href="#"
          className="block px-6 py-1.5 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          Help Center
        </a>
        <a
          href="#"
          className="block px-6 py-1.5 text-sm text-[#333] hover:bg-[#f7f7f7]"
        >
          What's new
        </a>
      </div>
    </div>
  );
}

// ---------- Page Headers ----------

const OVERVIEW_TABS = [
  "All",
  "New customers",
  "Returning customers",
  "Advertised products",
];

function SettingsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M6.5 1.5L6.1 3.3C5.7 3.5 5.3 3.7 5 3.9L3.2 3.3L1.7 5.9L3.1 7.2C3 7.5 3 7.7 3 8C3 8.3 3 8.5 3.1 8.8L1.7 10.1L3.2 12.7L5 12.1C5.3 12.3 5.7 12.5 6.1 12.7L6.5 14.5H9.5L9.9 12.7C10.3 12.5 10.7 12.3 11 12.1L12.8 12.7L14.3 10.1L12.9 8.8C13 8.5 13 8.3 13 8C13 7.7 13 7.5 12.9 7.2L14.3 5.9L12.8 3.3L11 3.9C10.7 3.7 10.3 3.5 9.9 3.3L9.5 1.5H6.5Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

export function OverviewPageHeader({
  activeTab,
  onTabChange,
  onNavigateToSettings,
  monthlyBudget = "$1,000",
}: {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onNavigateToSettings: () => void;
  monthlyBudget?: string;
}) {
  return (
    <div className="bg-fs-surface-primary">
      <div className="flex flex-col gap-1 px-6 pt-6">
        <div className="flex items-start gap-8">
          <div className="flex-1 pt-[5px]">
            <h1 className="text-[30px] leading-[38px] font-semibold text-[#333]">
              Promoted Listings
            </h1>
          </div>
          <div className="flex h-12 items-center gap-6">
            <button className="text-sm text-[#333] underline hover:text-[#757575]">
              Stop advertising
            </button>
            <Button
              variant="secondary"
              size="small"
              // icon={<SettingsIcon />}
              // iconPosition="end"
              iconProps={{
                Component: SettingsIcon,
                position: "end",
              }}
              onClick={onNavigateToSettings}
            >
              Advertising settings
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Tag label="Active" variant="success" />
          <span className="text-sm text-[#757575]">
            Renews Mar 1, 2026 with a monthly budget of {monthlyBudget}
          </span>
        </div>
      </div>
      <div className="mt-5 flex gap-12 border-b border-[#dfe0e1] px-6">
        {OVERVIEW_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => onTabChange(tab)}
            className={cn(
              "relative pb-3 text-sm",
              activeTab === tab
                ? "font-medium text-[#333] after:absolute after:right-0 after:bottom-0 after:left-0 after:h-[2px] after:bg-[#333]"
                : "text-[#757575] hover:text-[#333]"
            )}
          >
            {tab}
          </button>
        ))}
      </div>
    </div>
  );
}

export function SettingsPageHeader({
  onNavigateBack,
  isDirty,
  onSave,
  onUndo,
}: {
  onNavigateBack: () => void;
  isDirty?: boolean;
  onSave?: () => void;
  onUndo?: () => void;
}) {
  return (
    <div className="bg-fs-surface-primary border-b border-[#dfe0e1] px-10 pb-4">
      <div className="flex h-12 items-center py-2.5">
        <button
          onClick={onNavigateBack}
          className="flex items-center gap-1 text-sm text-[#333] hover:text-[#757575]"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M10 4L6 8L10 12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Promoted Listings
        </button>
      </div>
      <div className="flex items-start gap-8">
        <div className="flex-1 pt-[5px]">
          <h1 className="text-[30px] leading-[38px] font-semibold text-[#333]">
            Advertising settings
          </h1>
        </div>
        <div className="flex h-12 items-center gap-6">
          {isDirty && onUndo && (
            <button
              onClick={onUndo}
              className="text-sm text-[#333] underline hover:text-[#757575]"
            >
              Undo changes
            </button>
          )}
          <Button
            variant="primary"
            size="small"
            disabled={!isDirty}
            onClick={onSave}
          >
            Save
          </Button>
        </div>
      </div>
      <p className="mt-1 text-sm text-[#757575]">
        Configure your growth mode, budget, and audience.
      </p>
    </div>
  );
}

// ---------- Performance Overview (static screenshot from Figma) ----------
// Place scenario-specific screenshots in public/images/:
//   perf-overview.png          — default
//   perf-overview-budget.png   — budget-constrained
//   perf-overview-roas.png     — ROAS-constrained

const PERF_OVERVIEW_IMAGES: Record<string, string> = {
  default:
    "https://cdn.prerelease.faire.dev/fastly/fade7652e60b5888aa9fdc012746fb59cfb2e544f34d30e1ed07d9d4d9c17848.png",
  "budget-constrained":
    "https://cdn.prerelease.faire.dev/fastly/fade7652e60b5888aa9fdc012746fb59cfb2e544f34d30e1ed07d9d4d9c17848.png",
  "roas-constrained":
    "https://cdn.prerelease.faire.dev/fastly/11fae5ef5fcf2fbde4449217cdd37071748056e4efa9c5e77f1063ca5eba7da4.png",
};

export function PerformanceOverview({
  scenario = "default",
}: {
  scenario?: string;
}) {
  const src = PERF_OVERVIEW_IMAGES[scenario] ?? PERF_OVERVIEW_IMAGES.default;
  return (
    <img src={src} alt="Performance overview" className="w-full rounded-lg" />
  );
}

// ---------- Growth Mode Slider (A version – named labels) ----------

interface GrowthModeLevelA {
  label: string;
  description: string;
}

const GROWTH_LEVELS_A: GrowthModeLevelA[] = [
  {
    label: "Conservative",
    description:
      "Prioritize cost efficiency. Your ads will appear less frequently, helping keep your average cost per click lower.",
  },
  {
    label: "Moderate",
    description:
      "Balance visibility and cost. Your ads will appear at a moderate frequency, giving you steady exposure without significantly increasing costs.",
  },
  {
    label: "Competitive",
    description:
      "Drive growth. Your ads will appear more frequently, which may lead to a higher average cost per click.",
  },
  {
    label: "Aggressive",
    description:
      "Maximize reach. Your ads will appear as frequently as possible, which will likely increase your average cost per click.",
  },
];

// ---------- Growth Mode Slider (B version – numbered labels) ----------

interface GrowthModeLevelB {
  label: string;
  description: string;
}

const GROWTH_LEVELS_B: GrowthModeLevelB[] = [
  {
    label: "Level 1",
    description:
      "Prioritize cost efficiency. Your ads will appear less frequently, helping keep your average cost per click lower.",
  },
  {
    label: "Level 2",
    description:
      "Balance visibility and cost. Your average cost per click will be lower, but you may miss out on opportunities to reach customers. Best if you're testing ads for the first time.",
  },
  {
    label: "Level 3",
    description:
      "Drive growth. Your ads will appear more frequently, which may lead to a higher average cost per click.",
  },
  {
    label: "Level 4",
    description:
      "Maximize reach. Your ads will appear as frequently as possible, which will likely increase your average cost per click.",
  },
];

function GrowthModeLabelTooltip({
  tooltip,
  children,
}: {
  tooltip: string;
  children: ReactNode;
}) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) {
      return;
    }
    const triggerRect = trigger.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const left = triggerRect.left + triggerRect.width / 2 - panelRect.width / 2;
    setCoords({
      top: triggerRect.bottom + 10,
      left: Math.max(
        8,
        Math.min(left, window.innerWidth - panelRect.width - 8)
      ),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isVisible) {
      return;
    }
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isVisible, updatePosition]);

  return (
    <>
      <span
        ref={triggerRef}
        className="min-w-0 flex-1 overflow-hidden"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </span>
      {isVisible
        ? createPortal(
            <div
              ref={panelRef}
              role="tooltip"
              className="pointer-events-none fixed z-[700]"
              style={{ top: coords.top, left: coords.left }}
            >
              <div className="relative max-w-[280px] rounded-[4px] bg-[#333333] px-4 py-3 text-left text-[14px] leading-[20px] font-normal text-[#ffffff] shadow-[0_2px_8px_rgba(0,0,0,0.2)]">
                {tooltip}
                <span
                  aria-hidden
                  className="absolute bottom-full left-1/2 h-0 w-0 -translate-x-1/2 border-x-[6px] border-b-[6px] border-x-transparent border-b-[#333333]"
                />
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function gradientForStop(index: number): string {
  const stops: Record<number, string> = {
    0: "transparent",
    1: "linear-gradient(90deg, #5d2e37 0%, #a0533a 100%)",
    2: "linear-gradient(90deg, #5d2e37 0%, #8f4a3a 50%, #c1653c 100%)",
    3: "linear-gradient(90deg, #5d2e37 0%, #a0533a 33%, #c1653c 66%, #e3773e 100%)",
  };
  return stops[index] ?? "transparent";
}

function GrowthModeSlider({
  value,
  onChange,
  variant,
  descriptionPlacement = "side",
  descriptions,
  labelTooltips,
}: {
  value: number;
  onChange: (v: number) => void;
  variant: "A" | "B";
  descriptionPlacement?: "side" | "below" | "tooltip";
  descriptions?: string[];
  labelTooltips?: string[];
}) {
  const levels = variant === "A" ? GROWTH_LEVELS_A : GROWTH_LEVELS_B;
  const stopCount = levels.length;
  const thumbPercent = (value / (stopCount - 1)) * 100;

  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [dragPercent, setDragPercent] = useState<number | null>(null);
  const draggingRef = useRef(false);
  const [snapMode, setSnapMode] = useState<"drag" | null>(null);
  const [visualStop, setVisualStop] = useState(value);
  const gradientTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    clearTimeout(gradientTimerRef.current);
    if (dragging) {
      setVisualStop(value);
    } else {
      const duration = snapMode === "drag" ? 200 : 120;
      gradientTimerRef.current = setTimeout(
        () => setVisualStop(value),
        duration
      );
    }
    return () => clearTimeout(gradientTimerRef.current);
  }, [value, dragging, snapMode]);

  const clientXToRatio = useCallback((clientX: number) => {
    const el = trackRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const paddingX = rect.width * 0.125;
    const trackWidth = rect.width - paddingX * 2;
    const x = clientX - rect.left - paddingX;
    return Math.max(0, Math.min(1, x / trackWidth));
  }, []);

  const didDragRef = useRef(false);

  useEffect(() => {
    if (!dragging) return;
    didDragRef.current = false;
    const onMove = (e: MouseEvent) => {
      didDragRef.current = true;
      const ratio = clientXToRatio(e.clientX);
      setDragPercent(ratio * 100);
    };
    const onUp = (e: MouseEvent) => {
      const ratio = clientXToRatio(e.clientX);
      const nearest = Math.round(ratio * (stopCount - 1));
      const wasDrag = didDragRef.current;
      draggingRef.current = false;
      setDragging(false);
      setDragPercent(null);
      setSnapMode(wasDrag ? "drag" : null);
      onChange(nearest);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [dragging, clientXToRatio, stopCount, onChange]);

  const displayPercent = dragPercent !== null ? dragPercent : thumbPercent;
  const displayStop =
    dragPercent !== null ? (dragPercent / 100) * (stopCount - 1) : value;
  const snapDuration = snapMode === "drag" ? 200 : 120;

  const isBelow = descriptionPlacement === "below";
  const hideDescription =
    descriptionPlacement === "tooltip" || Boolean(labelTooltips);
  const description = descriptions?.[value] ?? levels[value].description;

  return (
    <div
      className={cn(
        "flex w-full",
        isBelow ? "flex-col gap-4" : "items-center gap-10"
      )}
    >
      <div
        className={cn(
          "flex flex-col",
          isBelow ? "w-full" : "w-[468px] flex-1",
          variant === "A" ? "gap-3" : ""
        )}
      >
        {/* Track area — percentage padding so endpoints center on first/last labels */}
        <div
          ref={trackRef}
          className={cn(
            "group/track relative flex h-8 cursor-pointer items-center select-none",
            dragging ? "is-dragging" : ""
          )}
          style={{ padding: "0 12.5%" }}
          onMouseDown={(e) => {
            e.preventDefault();
            setDragging(true);
            draggingRef.current = true;
          }}
        >
          <div className="pointer-events-none relative h-1 w-full">
            <div className="absolute inset-0 rounded-full bg-[#d2d5db]" />
            {displayPercent > 0 && (
              <div
                className="absolute top-0 left-0 h-full rounded-l-full"
                style={{
                  width: `${displayPercent}%`,
                  background: gradientForStop(
                    dragging ? Math.round(displayStop) : visualStop
                  ),
                  transition: dragging
                    ? "none"
                    : `width ${snapDuration}ms ease, background ${snapDuration}ms ease`,
                }}
              />
            )}
          </div>
          <div
            className={cn(
              "pointer-events-none absolute z-10 h-4 w-4 -translate-x-1/2 rounded-full",
              dragging
                ? "bg-(--slate-color-grey-1000) shadow-[0_0_1px_6px_rgba(93,46,55,0.4)]"
                : "bg-[#5d2e37] group-hover/track:shadow-[0_0_1px_6px_rgba(93,46,55,0.2)] group-active/track:bg-(--slate-color-grey-1000) group-active/track:shadow-[0_0_1px_6px_rgba(93,46,55,0.4)]"
            )}
            style={{
              left: `calc(12.5% + ${displayPercent}% * 75% / 100%)`,
              transition: dragging
                ? "box-shadow 150ms ease, background-color 150ms ease"
                : `left ${snapDuration}ms ease, box-shadow 150ms ease, background-color 150ms ease`,
            }}
          />
        </div>
        {/* Labels */}
        {variant === "A" ? (
          <div className="grid grid-cols-4">
            {levels.map((level, i) => {
              const tooltip = labelTooltips?.[i];
              const labelButton = (
                <button
                  type="button"
                  onClick={() => onChange(i)}
                  className={cn(
                    "w-full cursor-pointer text-center text-sm",
                    i === value ? "font-medium text-[#333]" : "text-[#757575]"
                  )}
                >
                  {tooltip ? (
                    <span className="underline decoration-dotted underline-offset-[3px]">
                      {level.label}
                    </span>
                  ) : (
                    level.label
                  )}
                </button>
              );
              return tooltip ? (
                <GrowthModeLabelTooltip key={i} tooltip={tooltip}>
                  {labelButton}
                </GrowthModeLabelTooltip>
              ) : (
                <span key={i} className="min-w-0 flex-1">
                  {labelButton}
                </span>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <div className="flex justify-between">
              {levels.map((_, i) => (
                <button
                  key={i}
                  onClick={() => onChange(i)}
                  className={cn(
                    "flex-1 cursor-pointer text-center text-sm",
                    i === value ? "font-medium text-[#333]" : "text-[#757575]"
                  )}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <div className="flex justify-between">
              <span className="flex-1 text-center text-xs text-[#757575]">
                Minimize cost
              </span>
              <span className="flex-1" />
              <span className="flex-1" />
              <span className="flex-1 text-center text-xs text-[#757575]">
                Maximize growth
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Description card — hidden when each stop has its own hover tooltip */}
      {hideDescription ? null : isBelow ? (
        <p className="rounded bg-[#f7f7f7] px-3 py-2.5 text-xs leading-4 text-[#333]">
          <span className="font-medium">{levels[value].label}:</span>{" "}
          {description}
        </p>
      ) : (
        <div className="flex min-h-[104px] flex-1 flex-col justify-center rounded bg-[#f7f7f7] p-4">
          <p className="mb-2 text-xs font-medium text-[#333]">
            {levels[value].label}
          </p>
          <p className="text-xs leading-4 text-[#333]">{description}</p>
        </div>
      )}
    </div>
  );
}

/**
 * Heading styles for the settings modules. The `h3` variant renders one size
 * smaller so it can sit under a larger `h2` section header (e.g. "General
 * settings"); the default `h2` keeps the original standalone size.
 */
const settingsModuleHeadingClass = (level: "h2" | "h3") =>
  level === "h3"
    ? "text-[20px] leading-[28px] font-medium text-[#333]"
    : "text-[22px] leading-[32px] font-medium text-[#333]";

export function GrowthModeModule({
  variant,
  value,
  onChange,
  roasConstrained = false,
  headingLevel: Heading = "h2",
  descriptionPlacement,
  descriptions,
  labelTooltips,
  headingAccessory,
  showLearnMore = true,
  className,
  headingClassName,
  descriptionClassName,
}: {
  variant: "A" | "B";
  value: number;
  onChange: (v: number) => void;
  roasConstrained?: boolean;
  headingLevel?: "h2" | "h3";
  descriptionPlacement?: "side" | "below" | "tooltip";
  descriptions?: string[];
  labelTooltips?: string[];
  headingAccessory?: ReactNode;
  showLearnMore?: boolean;
  className?: string;
  headingClassName?: string;
  descriptionClassName?: string;
}) {
  return (
    <div
      className={cn(
        "bg-fs-surface-primary flex flex-col gap-6 rounded border border-[#dfe0e1] p-8",
        className
      )}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Heading
            className={cn(
              settingsModuleHeadingClass(Heading),
              headingClassName
            )}
          >
            Growth mode
          </Heading>
          {headingAccessory}
        </div>
        <p
          className={cn(
            "max-w-[600px] text-sm text-[#333]",
            descriptionClassName
          )}
        >
          Choose how aggressively you want to promote your products. Higher
          settings increase the visibility of your ads, but may result in a
          higher average cost per click.
          {showLearnMore ? (
            <>
              {" "}
              <a
                href="https://www.faire.com/en/support/articles/47382088282779"
                className="underline"
              >
                Learn more
              </a>
            </>
          ) : null}
        </p>
      </div>
      <GrowthModeSlider
        value={value}
        onChange={onChange}
        variant={variant}
        descriptionPlacement={descriptionPlacement}
        descriptions={descriptions}
        labelTooltips={labelTooltips}
      />
      {roasConstrained && (
        <InlineWarningTag label="Current setting is limiting reach" />
      )}
    </div>
  );
}

export function getGrowthModeLabel(variant: "A" | "B", index: number): string {
  const levels = variant === "A" ? GROWTH_LEVELS_A : GROWTH_LEVELS_B;
  return levels[index]?.label ?? "";
}

// ---------- Monthly Budget Module ----------

const BUDGET_RECOMMENDATIONS: Array<{
  shared: string;
  newCustomer: string;
  returning: string;
}> = [
  { shared: "$500–$600", newCustomer: "$200–$400", returning: "$200–$400" },
  { shared: "$550–$700", newCustomer: "$250–$450", returning: "$250–$450" },
  { shared: "$600–$800", newCustomer: "$300–$500", returning: "$300–$500" },
  { shared: "$700–$1,000", newCustomer: "$350–$600", returning: "$350–$600" },
];

const GROWTH_MODE_NAMES = [
  "Conservative",
  "Moderate",
  "Competitive",
  "Aggressive",
];

function BudgetRecommendation({
  title,
  range,
}: {
  title: string;
  range: string;
}) {
  return (
    <div className="flex-1 rounded bg-[#e2e7f0] p-4">
      <p className="mb-1 text-xs font-medium text-[#333]">{title}</p>
      <p className="text-xs leading-4 text-[#333]">
        Based on your sales history on Faire and selected growth mode, we
        recommend setting your budget to{" "}
        <span className="font-medium">{range}</span> to maximize the results
        from your ads.
      </p>
    </div>
  );
}

export interface BudgetState {
  separateBudget: boolean;
  overallBudget: string;
  newCustomerBudget: string;
  returningBudget: string;
}

function WarningTriangleIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className={className}
    >
      <path
        d="M8.00004 5.71435V9.14293M8.66671 12.6668C8.66671 13.035 8.36823 13.3335 8.00004 13.3335C7.63185 13.3335 7.33337 13.035 7.33337 12.6668C7.33337 12.2986 7.63185 12.0002 8.00004 12.0002C8.36823 12.0002 8.66671 12.2986 8.66671 12.6668ZM9.02182 1.20353C8.9268 1.01396 8.78092 0.854551 8.60048 0.743152C8.42004 0.631753 8.21216 0.572754 8.0001 0.572754C7.78805 0.572754 7.58017 0.631753 7.39973 0.743152C7.21929 0.854551 7.07341 1.01396 6.97839 1.20353L0.692676 13.775C0.605631 13.9492 0.564539 14.1427 0.573301 14.3372C0.582063 14.5318 0.640387 14.7208 0.74274 14.8865C0.845092 15.0522 0.988078 15.1889 1.15813 15.2838C1.32818 15.3787 1.51966 15.4286 1.71439 15.4287H14.2858C14.4805 15.4286 14.672 15.3787 14.8421 15.2838C15.0121 15.1889 15.1551 15.0522 15.2575 14.8865C15.3598 14.7208 15.4181 14.5318 15.4269 14.3372C15.4357 14.1427 15.3946 13.9492 15.3075 13.775L9.02182 1.20353Z"
        stroke="#907C3A"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ConstraintBanner({
  title,
  body,
  linkLabel,
  onLinkClick,
}: {
  title: string;
  body: string;
  linkLabel: string;
  onLinkClick: () => void;
}) {
  return (
    <div className="flex items-start gap-4 rounded bg-[#f6efdb] p-4">
      <WarningTriangleIcon className="mt-0.5 shrink-0" />
      <div>
        <p className="text-sm font-medium text-[#333]">{title}</p>
        <p className="text-sm text-[#333]">
          {body}{" "}
          <button onClick={onLinkClick} className="font-medium underline">
            {linkLabel}
          </button>
        </p>
      </div>
    </div>
  );
}

export function InlineWarningTag({ label }: { label: string }) {
  return (
    <div className="flex w-fit items-center gap-2 rounded-sm bg-[#f6efdb] px-2 py-1">
      <WarningTriangleIcon />
      <span className="text-xs font-medium text-[#333]">{label}</span>
    </div>
  );
}

export function MonthlyBudgetModule({
  budget,
  onBudgetChange,
  growthMode = 0,
  budgetConstrained = false,
  budgetInputRef,
  headingLevel: Heading = "h2",
  description = (
    <>
      The maximum amount you want to spend on ads per month. We may spend more
      on days with better opportunities, and less on days with fewer
      opportunities.{" "}
      <span className="font-medium">
        You&apos;ll only be charged when retailers click your listings.
      </span>
    </>
  ),
}: {
  budget: BudgetState;
  onBudgetChange: (next: Partial<BudgetState>) => void;
  growthMode?: number;
  budgetConstrained?: boolean;
  budgetInputRef?: React.RefObject<HTMLInputElement | null>;
  headingLevel?: "h2" | "h3";
  description?: React.ReactNode;
}) {
  const { separateBudget, overallBudget, newCustomerBudget, returningBudget } =
    budget;
  const rec = BUDGET_RECOMMENDATIONS[growthMode] ?? BUDGET_RECOMMENDATIONS[0];
  const modeName = GROWTH_MODE_NAMES[growthMode] ?? "Conservative";
  const totalSeparate =
    (parseInt(newCustomerBudget) || 0) + (parseInt(returningBudget) || 0);

  return (
    <div className="bg-fs-surface-primary flex flex-col gap-6 rounded border border-[#dfe0e1] p-8">
      <div className="flex flex-col gap-1">
        <div className="flex items-start justify-between">
          <Heading className={settingsModuleHeadingClass(Heading)}>
            Monthly budget
          </Heading>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#333]">
              Separate budget by audience
            </span>
            <Toggle
              checked={separateBudget}
              onChange={() =>
                onBudgetChange({ separateBudget: !separateBudget })
              }
            />
          </div>
        </div>
        <p className="max-w-[600px] text-sm text-[#333]">{description}</p>
      </div>

      {!separateBudget ? (
        <div className="flex items-start gap-10">
          <div className="flex-1">
            <label className="mb-1 block text-sm text-[#333]">
              Overall budget
            </label>
            <div className="bg-fs-surface-primary flex h-10 w-full max-w-[400px] items-center rounded border border-[#dfe0e1] px-4">
              <span className="mr-2 text-sm text-[#333]">$</span>
              <input
                ref={budgetInputRef}
                type="text"
                value={overallBudget}
                onChange={(e) =>
                  onBudgetChange({
                    overallBudget: e.target.value.replace(/[^0-9]/g, ""),
                  })
                }
                className="flex-1 bg-transparent text-sm text-[#333] outline-none"
              />
            </div>
            {budgetConstrained && (
              <div className="mt-2">
                <InlineWarningTag label="Budget is limiting reach" />
              </div>
            )}
          </div>
          <div className="w-[468px] flex-shrink-0 pt-5">
            <BudgetRecommendation
              title={`Recommended budget for ${modeName} growth mode`}
              range={rec.shared}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-10">
            <div className="flex-1">
              <label className="mb-1 block text-sm text-[#333]">
                New customer budget
              </label>
              <div className="bg-fs-surface-primary flex h-10 w-full max-w-[400px] items-center rounded border border-[#dfe0e1] px-4">
                <span className="mr-2 text-sm text-[#333]">$</span>
                <input
                  type="text"
                  value={newCustomerBudget}
                  onChange={(e) =>
                    onBudgetChange({
                      newCustomerBudget: e.target.value.replace(/[^0-9]/g, ""),
                    })
                  }
                  className="flex-1 bg-transparent text-sm text-[#333] outline-none"
                />
              </div>
            </div>
            <div className="w-[468px] flex-shrink-0 pt-5">
              <BudgetRecommendation
                title={`Recommended new customer budget for ${modeName} growth mode`}
                range={rec.newCustomer}
              />
            </div>
          </div>
          <div className="flex items-start gap-10">
            <div className="flex-1">
              <label className="mb-1 block text-sm text-[#333]">
                Returning customer budget
              </label>
              <div className="bg-fs-surface-primary flex h-10 w-full max-w-[400px] items-center rounded border border-[#dfe0e1] px-4">
                <span className="mr-2 text-sm text-[#333]">$</span>
                <input
                  type="text"
                  value={returningBudget}
                  onChange={(e) =>
                    onBudgetChange({
                      returningBudget: e.target.value.replace(/[^0-9]/g, ""),
                    })
                  }
                  className="flex-1 bg-transparent text-sm text-[#333] outline-none"
                />
              </div>
            </div>
            <div className="w-[468px] flex-shrink-0 pt-5">
              <BudgetRecommendation
                title={`Recommended returning customer budget for ${modeName} growth mode`}
                range={rec.returning}
              />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <p className="text-sm font-medium text-[#333]">
              Total monthly budget: ${totalSeparate}
            </p>
            <p className="text-sm text-[#921100]">
              Separating your budgets may limit opportunities and reduce overall
              ad performance.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Target Audience Module ----------

export type Audience = "both" | "new";

export function TargetAudienceModule({
  audience,
  onAudienceChange,
  headingLevel: Heading = "h2",
}: {
  audience: Audience;
  onAudienceChange: (v: Audience) => void;
  headingLevel?: "h2" | "h3";
}) {
  const options: Array<{
    value: "both" | "new";
    title: string;
    description: string;
    tag?: string;
  }> = [
    {
      value: "both",
      title: "New and returning customers",
      description:
        "Acquire new customers and drive reorders from returning customers.",
      tag: "Recommended",
    },
    {
      value: "new",
      title: "New customers only",
      description:
        "Focus exclusively on acquiring new customers with boosted visibility to retailers that haven't shopped with you before.",
    },
  ];

  return (
    <div className="bg-fs-surface-primary flex flex-col gap-6 rounded border border-[#dfe0e1] p-8">
      <div className="flex flex-col gap-1">
        <Heading className={settingsModuleHeadingClass(Heading)}>
          Target audience
        </Heading>
        <p className="max-w-[600px] text-sm text-[#333]">
          Choose which retailers you want to reach with your promoted listings.
        </p>
      </div>
      <div className="flex gap-4">
        {options.map((opt) => {
          const selected = audience === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onAudienceChange(opt.value)}
              className={cn(
                "flex flex-1 cursor-pointer items-start gap-4 rounded border p-4 text-left transition-colors",
                selected
                  ? "border-(--slate-color-grey-1000)"
                  : "border-[#dfe0e1] hover:border-[#999]"
              )}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 shrink-0">
                    <div
                      className={cn(
                        "flex h-4 w-4 items-center justify-center rounded-full border",
                        selected
                          ? "border-(--slate-color-grey-1000)"
                          : "border-[#333]"
                      )}
                    >
                      {selected && (
                        <div className="h-2 w-2 rounded-full bg-(--slate-color-grey-1000)" />
                      )}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-[#333]">
                        {opt.title}
                      </span>
                      {opt.tag ? (
                        <span className="inline-flex shrink-0 items-center rounded border border-[#c5dcc5] bg-[#eaf3ea] px-2 py-0.5 text-[11px] leading-none font-medium text-[#2d4a38]">
                          {opt.tag}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-sm leading-snug text-[#555]">
                      {opt.description}
                    </p>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Confirm Settings Modal ----------

export function ConfirmSettingsModal({
  growthModeLabel,
  budgetDisplay,
  audienceLabel,
  onCancel,
  onConfirm,
}: {
  growthModeLabel: string;
  budgetDisplay: string;
  audienceLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-(--slate-color-grey-1000)/40"
        onClick={onCancel}
      />
      <div className="bg-fs-surface-primary relative mx-4 flex w-full max-w-[560px] flex-col rounded-lg shadow-xl">
        <div className="flex items-start justify-between p-6 pb-4">
          <h2 className="text-[22px] leading-[28px] font-semibold text-[#333]">
            Confirm advertising settings
          </h2>
          <button
            onClick={onCancel}
            className="-mt-1 -mr-1 flex h-8 w-8 items-center justify-center rounded-full hover:bg-[#f7f7f7]"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4L12 12M12 4L4 12"
                stroke="#333"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="px-6">
          <p className="text-sm leading-5 text-[#333]">
            Your new advertising settings will take effect within 24 hours. For
            best results, we recommend running your campaign for a month before
            evaluating changes in performance.
          </p>
        </div>

        <div className="mt-6 flex flex-col px-6">
          <div className="border-t border-[#dfe0e1] py-4">
            <p className="text-sm text-[#757575]">Growth mode</p>
            <p className="mt-0.5 text-sm font-medium text-[#333]">
              {growthModeLabel}
            </p>
          </div>
          <div className="border-t border-[#dfe0e1] py-4">
            <p className="text-sm text-[#757575]">Monthly budget</p>
            <p className="mt-0.5 text-sm font-medium text-[#333]">
              {budgetDisplay}
            </p>
            <p className="mt-1 text-sm text-[#757575]">
              Your budget will be prorated for February and will automatically
              renew on a recurring monthly basis starting Mar 1, 2026.
            </p>
          </div>
          <div className="border-t border-[#dfe0e1] py-4">
            <p className="text-sm text-[#757575]">Audience</p>
            <p className="mt-0.5 text-sm font-medium text-[#333]">
              {audienceLabel}
            </p>
          </div>
          <div className="border-t border-[#dfe0e1] pt-4 pb-2">
            <p className="text-sm text-[#333]">
              By clicking confirm settings, I agree to the Promoted Listings{" "}
              <a href="#" className="underline">
                Terms & Conditions.
              </a>
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 pt-4">
          <button
            onClick={onCancel}
            className="rounded-lg border border-[#dfe0e1] px-6 py-2.5 text-sm text-[#333] transition-colors hover:bg-[#f7f7f7]"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="text-fs-text-primary-inverse rounded-lg bg-[#333] px-6 py-2.5 text-sm transition-colors hover:bg-[#1a1a1a]"
          >
            Confirm settings
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Toast ----------

export function Toast({
  message,
  visible,
}: {
  message: string;
  visible: boolean;
}) {
  return (
    <div
      className={cn(
        "fixed bottom-6 left-1/2 z-50 -translate-x-1/2 transition-all duration-300",
        visible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-4 opacity-0"
      )}
    >
      <div className="text-fs-text-primary-inverse rounded-lg bg-[#333] px-6 py-3 text-sm whitespace-nowrap shadow-lg">
        {message}
      </div>
    </div>
  );
}

// ---------- Save Bar ----------

export function SaveBar() {
  return (
    <div className="bg-fs-surface-primary flex items-center justify-between rounded border border-[#dfe0e1] p-6">
      <p className="text-sm text-[#757575]">
        Changes will apply to next month's billing cycle.
      </p>
      <div className="flex gap-3">
        <button className="rounded-lg border border-[#dfe0e1] px-6 py-2.5 text-sm text-[#333] transition-colors hover:bg-[#f7f7f7]">
          Cancel
        </button>
        <button className="text-fs-text-primary-inverse rounded-lg bg-[#333] px-6 py-2.5 text-sm transition-colors hover:bg-[#1a1a1a]">
          Save changes
        </button>
      </div>
    </div>
  );
}
